import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../hooks/useChat';
import Icon from '../AppIcon';
import toast from 'react-hot-toast';

const AUSTRALIAN_MARKET_DATA = {
  NSW: {
    medianPrice: '$1,185,000',
    yoyGrowth: '+6.2%',
    trend: 'rising',
    rbaImpact: 'Rate cuts in 2025 boosted borrowing capacity',
    outlook: 'Moderate growth expected through 2026'
  },
  VIC: {
    medianPrice: '$895,000',
    yoyGrowth: '+2.1%',
    trend: 'stable',
    rbaImpact: 'Melbourne recovering after 2023-24 correction',
    outlook: 'Gradual recovery with infrastructure investment'
  },
  QLD: {
    medianPrice: '$820,000',
    yoyGrowth: '+11.4%',
    trend: 'rising',
    rbaImpact: 'Strong interstate migration driving demand',
    outlook: 'Olympics 2032 infrastructure boosting long-term value'
  },
  WA: {
    medianPrice: '$745,000',
    yoyGrowth: '+19.6%',
    trend: 'rising',
    rbaImpact: 'Mining boom and low supply driving prices',
    outlook: 'Continued growth but pace expected to moderate'
  },
  SA: {
    medianPrice: '$720,000',
    yoyGrowth: '+13.8%',
    trend: 'rising',
    rbaImpact: 'Affordability advantage attracting interstate buyers',
    outlook: 'Strong fundamentals with defence industry growth'
  },
  ACT: {
    medianPrice: '$865,000',
    yoyGrowth: '+4.5%',
    trend: 'stable',
    rbaImpact: 'Government employment providing stable demand',
    outlook: 'Steady growth aligned with public sector expansion'
  },
  TAS: {
    medianPrice: '$620,000',
    yoyGrowth: '+3.2%',
    trend: 'stable',
    rbaImpact: 'Tourism and lifestyle appeal sustaining demand',
    outlook: 'Moderate growth with limited supply'
  },
  NT: {
    medianPrice: '$510,000',
    yoyGrowth: '+1.8%',
    trend: 'stable',
    rbaImpact: 'Defence spending supporting local economy',
    outlook: 'Stable with potential upside from resource projects'
  }
};

const SUBURB_INSIGHTS = {
  Sydney: { tier: 'premium', competition: 'high', daysOnMarket: 28, clearanceRate: '72%' },
  Melbourne: { tier: 'premium', competition: 'moderate', daysOnMarket: 35, clearanceRate: '65%' },
  Brisbane: { tier: 'mid', competition: 'high', daysOnMarket: 22, clearanceRate: '68%' },
  Perth: { tier: 'mid', competition: 'very high', daysOnMarket: 14, clearanceRate: '78%' },
  Adelaide: { tier: 'mid', competition: 'high', daysOnMarket: 18, clearanceRate: '74%' },
  Canberra: { tier: 'mid', competition: 'moderate', daysOnMarket: 30, clearanceRate: '66%' },
  Hobart: { tier: 'mid', competition: 'moderate', daysOnMarket: 32, clearanceRate: '62%' },
  Darwin: { tier: 'affordable', competition: 'low', daysOnMarket: 45, clearanceRate: '55%' }
};

const MarketTrendSummary = ({ suburb, state, city, propertyPrice, propertyType }) => {
  const [summary, setSummary] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4.1-mini', false);

  useEffect(() => {
    if (error) toast?.error('Unable to generate market analysis. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const parsed = JSON.parse(response);
        setSummary(parsed);
      } catch {
        setSummary({ rawText: response });
      }
    }
  }, [response, isLoading]);

  const generateSummary = useCallback(() => {
    const location = suburb || city || 'the area';
    const stateCode = state?.toUpperCase();
    const stateData = AUSTRALIAN_MARKET_DATA?.[stateCode] || AUSTRALIAN_MARKET_DATA?.['NSW'];
    const suburbData = SUBURB_INSIGHTS?.[location] || SUBURB_INSIGHTS?.[city] || null;

    const systemPrompt = `You are an expert Australian real estate market analyst with deep knowledge of all Australian states and territories. You provide data-driven, concise market insights for property buyers. Always respond with valid JSON only.`;

    const userPrompt = `Generate a personalized market trend summary and buyer timing recommendation for a property buyer.

Property Details:
- Location: ${location}, ${stateCode || state}
- Property Type: ${propertyType || 'residential'}
- Listed Price: ${propertyPrice ? `AUD ${new Intl.NumberFormat('en-AU')?.format(propertyPrice)}` : 'Not specified'}

Current ${stateCode || state} Market Data (2025):
- State Median Price: ${stateData?.medianPrice}
- Year-on-Year Growth: ${stateData?.yoyGrowth}
- Market Trend: ${stateData?.trend}
- RBA Impact: ${stateData?.rbaImpact}
- 2026 Outlook: ${stateData?.outlook}
${suburbData ? `
Suburb-Level Data for ${location}:
- Market Tier: ${suburbData?.tier}
- Buyer Competition: ${suburbData?.competition}
- Average Days on Market: ${suburbData?.daysOnMarket} days
- Auction Clearance Rate: ${suburbData?.clearanceRate}` : ''}

Respond ONLY with this exact JSON structure (no markdown, no extra text):
{
  "marketTrend": "brief 1-sentence trend description",
  "buyingConditions": "good" | "fair" | "challenging",
  "timingAdvice": "2-3 sentence advice on whether now is a good time to buy",
  "priceGrowthPrediction": "1-2 sentence price growth prediction for 12-24 months",
  "keyInsights": ["insight 1", "insight 2", "insight 3"],
  "riskFactors": ["risk 1", "risk 2"],
  "buyerTip": "one actionable tip specific to this location and market"
}`;

    sendMessage(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      { max_completion_tokens: 600 }
    );
    setHasGenerated(true);
  }, [suburb, state, city, propertyPrice, propertyType, sendMessage]);

  const conditionConfig = {
    good: { color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30', icon: 'TrendingUp', label: 'Good Time to Buy' },
    fair: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', icon: 'Minus', label: 'Fair Conditions' },
    challenging: { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30', icon: 'TrendingDown', label: 'Challenging Market' }
  };

  const condition = conditionConfig?.[summary?.buyingConditions] || conditionConfig?.fair;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="BarChart2" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm md:text-base">AI Market Trend Analysis</h3>
            <p className="text-xs text-muted-foreground">
              {suburb || city || 'Property'}{state ? `, ${state}` : ''} · Powered by OpenAI
            </p>
          </div>
        </div>
        {!hasGenerated && !isLoading && (
          <button
            onClick={generateSummary}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="Sparkles" size={13} color="currentColor" />
            Analyse Market
          </button>
        )}
        {hasGenerated && !isLoading && summary && (
          <button
            onClick={generateSummary}
            className="flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground text-xs font-medium rounded-lg hover:bg-muted/80 transition-colors"
          >
            <Icon name="RefreshCw" size={13} color="currentColor" />
            Refresh
          </button>
        )}
      </div>
      {!hasGenerated && !isLoading && (
        <div className="p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Icon name="TrendingUp" size={26} color="var(--color-primary)" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">Get AI-Powered Market Insights</p>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            Personalised analysis of market trends, price growth predictions, and buyer timing recommendations for {suburb || city || 'this area'}.
          </p>
        </div>
      )}
      {isLoading && (
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <p className="text-sm text-muted-foreground">Analysing {suburb || city || 'local'} market conditions...</p>
          </div>
          <div className="space-y-2.5">
            {[80, 60, 70, 50]?.map((w, i) => (
              <div key={i} className={`h-3 bg-muted rounded animate-pulse`} style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      )}
      {summary && !isLoading && (
        <div className="p-4 md:p-5 space-y-4">
          {/* Buying Conditions Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${condition?.bg} ${condition?.border}`}>
            <Icon name={condition?.icon} size={14} color="currentColor" className={condition?.color} />
            <span className={`text-xs font-semibold ${condition?.color}`}>{condition?.label}</span>
          </div>

          {/* Market Trend */}
          {summary?.marketTrend && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Market Trend</p>
              <p className="text-sm text-foreground">{summary?.marketTrend}</p>
            </div>
          )}

          {/* Timing Advice */}
          {summary?.timingAdvice && (
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Icon name="Clock" size={13} color="var(--color-primary)" />
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">Buyer Timing Advice</p>
              </div>
              <p className="text-sm text-foreground leading-relaxed">{summary?.timingAdvice}</p>
            </div>
          )}

          {/* Price Growth Prediction */}
          {summary?.priceGrowthPrediction && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">12–24 Month Price Outlook</p>
              <p className="text-sm text-foreground">{summary?.priceGrowthPrediction}</p>
            </div>
          )}

          {/* Key Insights */}
          {summary?.keyInsights?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Market Insights</p>
              <ul className="space-y-1.5">
                {summary?.keyInsights?.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={13} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risk Factors */}
          {summary?.riskFactors?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Risk Factors</p>
              <ul className="space-y-1.5">
                {summary?.riskFactors?.map((risk, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={13} color="#f59e0b" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Buyer Tip */}
          {summary?.buyerTip && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Lightbulb" size={13} color="var(--color-primary)" />
                <p className="text-xs font-semibold text-primary">Buyer Tip</p>
              </div>
              <p className="text-sm text-foreground">{summary?.buyerTip}</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground pt-1">
            AI-generated analysis based on 2025 Australian market data. Not financial advice.
          </p>
        </div>
      )}
    </div>
  );
};

export default MarketTrendSummary;
